/**
 * Which query decides what is IN the lens.
 *
 * A lens can hold several collections — the subject one and the shell's
 * outline or siblings beside it — and only one of them decides store
 * membership. This walk finds it: the first `collection` whose `source` is
 * absent or `'subject'` and which declares a query. Specs are validated before
 * they reach here, so it only ever selects a declared query and never
 * interprets an authored key.
 *
 * **It stops at the first one, and that is a limit worth naming.** The host
 * builds ONE scope per lens from what this returns, so a second subject
 * collection's `filter`, `sort` or `groupBy` is never sent to the store at
 * all. That is fine for every lens the generator produces today — there is one
 * subject collection in each — and it is exactly what stands between the
 * `viewState` mechanism and Coda's *"a control that drives queries in several
 * regions"*: the slot value would reach one region and no other. Task 0081.
 *
 * It lives in its own file so that limit is CHECKABLE. It was a closure inside
 * `main.ts`, which no test can reach, and a claim about what it does could
 * only ever be a claim about what the code looked like.
 */
import type { Scope } from '@morph/store';
import type { TLayoutSpec } from '@morph/spec';

/** One subject collection, and the query it wants answered. */
export interface SubjectRegion {
  /**
   * The collection's authored `id` — how the host names this region's rows back
   * to the renderer, which carries the same value as `IRNode.specId`.
   *
   * @Codex's ruling on task 0081: not an ordinal and not a structural path.
   * *"It would make the address change when a collection is moved or when an
   * ancestor or sibling is inserted. That creates the precise silent re-pairing
   * risk the key exists to prevent during ordinary layout editing."* The id is
   * required on every node, `validate.ts` refuses `duplicate-id`, and both
   * sides derive it independently from the same validated node — so this walk
   * and `compile.ts`'s need no shared traversal order.
   */
  readonly regionKey: string;
  /**
   * Absent when the collection declares none.
   *
   * Such a region still has to be NAMED. The renderer refuses to draw a subject
   * collection the host opted in and then did not name — deliberately, because
   * falling back to the subject conceals an addressing defect as an apparently
   * unfiltered region — and the IR cannot tell it which collections declared a
   * query, because `filter` never reaches the IR. So the host enumerates every
   * subject collection and hands the query-less ones the lens's own rows.
   */
  readonly query?: Scope['query'];
}

/**
 * Every subject collection that declares a query, in spec order.
 *
 * What a host that queries PER REGION needs, which is what Coda's *"a control
 * whose value drives queries in several regions"* requires: one scope each,
 * deduplicated by `scopeKey`, each result set named back by `regionKey`.
 * `membershipQuery` takes the first, which is what the host did until there was
 * a second region to have.
 *
 * A collection with an id but no query is skipped: it ranges over whatever the
 * subject is and has asked the store for nothing, so there is no scope to
 * build for it and the renderer's own fallback is the right answer.
 */
export function subjectRegions(spec: TLayoutSpec | undefined): SubjectRegion[] {
  const out: SubjectRegion[] = [];
  const visit = (value: unknown): void => {
    if (value === null || typeof value !== 'object') return;
    const node = value as Record<string, unknown>;
    if (node.kind === 'collection'
      && (node.source === undefined || node.source === 'subject')
      && typeof node.id === 'string' && node.id) {
      out.push({
        regionKey: node.id,
        ...(node.query === undefined ? {} : { query: node.query as Scope['query'] }),
      });
      // No `return`: a collection may hold another one, and a walk that stops
      // at the outer one cannot report what it skipped.
    }
    for (const child of Object.values(node)) visit(child);
  };
  if (spec) visit(spec.root);
  return out;
}

/**
 * Pair every subject region with the scope that answers it.
 *
 * The callback receives `undefined` for a query-less region on purpose. The
 * base scope is still a distinct answer when another region owns the lens's
 * first query; reusing that queried answer silently filters the query-less
 * region by its neighbour.
 */
export function subjectRegionScopes<T>(
  spec: TLayoutSpec | undefined,
  scopeFor: (query: Scope['query'] | undefined) => T,
): { readonly regionKey: string; readonly scope: T }[] {
  return subjectRegions(spec).map((region) => ({
    regionKey: region.regionKey,
    scope: scopeFor(region.query),
  }));
}

export interface SubjectRegionAnswer<T> {
  readonly items: readonly T[];
  readonly total?: number;
}

/**
 * Read and name the planned region scopes, deduplicating identical questions.
 *
 * The optional seed is the main lens answer the host already read. Keeping the
 * cache here makes the base-vs-filtered distinction, totals, and duplicate
 * reads one testable contract instead of an untestable closure in `main.ts`.
 */
export async function answerSubjectRegionScopes<S, T>(
  regions: readonly { readonly regionKey: string; readonly scope: S }[],
  keyOf: (scope: S) => string,
  read: (scope: S) => Promise<SubjectRegionAnswer<T>>,
  seed?: { readonly scope: S; readonly answer: SubjectRegionAnswer<T> },
): Promise<{
  readonly sets: Record<string, readonly T[]>;
  readonly totals: Record<string, number>;
}> {
  const answered = new Map<string, SubjectRegionAnswer<T>>();
  if (seed) answered.set(keyOf(seed.scope), seed.answer);
  const sets: Record<string, readonly T[]> = {};
  const totals: Record<string, number> = {};
  for (const region of regions) {
    const key = keyOf(region.scope);
    let answer = answered.get(key);
    if (!answer) {
      answer = await read(region.scope);
      answered.set(key, answer);
    }
    sets[region.regionKey] = answer.items;
    if (answer.total !== undefined) totals[region.regionKey] = answer.total;
  }
  return { sets, totals };
}

/** The subset that asked the store for something. */
export function subjectQueries(spec: TLayoutSpec | undefined): SubjectRegion[] {
  return subjectRegions(spec).filter((r) => r.query !== undefined);
}

/** The one query the host sends to the store when it sends one. */
export function membershipQuery(spec: TLayoutSpec | undefined): Scope['query'] | undefined {
  return subjectQueries(spec)[0]?.query;
}

/**
 * The branches a declared partition withholds while its control is empty.
 *
 * **This EXECUTES a declaration. It does not derive one.** That distinction is
 * the whole of @Codex's ruling on decision 0020, and the difference between
 * this function and the one it replaces.
 *
 * The version before it compared the regions' effective queries and held back
 * whichever would repeat, argued as the deduplication `answerSubjectRegionScopes`
 * already performs. The ruling drew the line exactly:
 *
 * > *`answerSubjectRegionScopes` ... caches one store answer for identical scope
 * > keys but still assigns that answer to EVERY DECLARED REGION. It changes the
 * > number of reads, not the structure or meaning of the lens.*
 *
 * Sharing an answer among requesters is not removing a requester, and *"query
 * equality licenses sharing an answer; it does not license suppressing one of
 * the requesters."* Two regions may ask one question and differ in placement,
 * labelling or item layout.
 *
 * So the spec now says which regions are branches, which slot drives them, and
 * which one stands alone until it has a value — and this reads that and nothing
 * else. It performs no query comparison, so it cannot be right or wrong about
 * complementarity; it can only be obeying the document or not.
 *
 * Requirement 5 of the ruling is the `slotIsSet` line: once the slot holds a
 * value, **every declared branch is evaluated and drawn normally**, so this
 * returns nothing at all.
 */
export function withheldBranches(
  spec: TLayoutSpec | undefined,
  slotIsSet: (slot: number) => boolean,
): readonly string[] {
  const partition = (spec as { partition?: {
    valueFrom: number; branches: readonly string[]; unset: { show: string };
  } } | undefined)?.partition;
  if (!partition) return [];
  if (slotIsSet(partition.valueFrom)) return [];
  return partition.branches.filter((b) => b !== partition.unset.show);
}
